import { numberHelpers } from '@approvalmax/utils';
import uniq from 'lodash/uniq';
import { backend, domain } from 'modules/data';
import { GetNetSuiteCustomer } from 'shared/data/webApp/v2';
import { useNetSuiteAttachmentsFieldSettings, useNetSuiteInvoiceFieldSettings } from 'shared/hooks';

import { CustomFieldsState, NetSuiteInvoiceListItemExpanded } from '../data/types';
import {
    checkInvoiceListItemIsEmpty,
    getCustomFieldsTransfer,
    mapCustomerAddresses,
    validateCustomFields,
} from '../helpers';
import { isFieldMandatory, isMandatoryFieldEmpty } from '../NetSuiteRequestForm/NetSuiteRequestForm.helpers';
import { messages } from './NetSuiteInvoiceRequestForm.messages';
import { NetSuiteInvoiceFields } from './NetSuiteInvoiceRequestForm.types';

export const mapInvoiceItemLinesTransfer = (itemLines: NetSuiteInvoiceListItemExpanded[]) =>
    itemLines.reduce<backend.transfers.IntegrationNetSuiteInvoiceItemLinesItem[]>((lines, itemLine) => {
        if (!checkInvoiceListItemIsEmpty(itemLine)) {
            lines.push({
                unitsId: itemLine.units?.id ?? null,
                rate: itemLine.unitPrice,
                itemId: itemLine.item?.id ?? null,
                description: itemLine.description,
                quantity: itemLine.quantity,
                priceLevelId: itemLine.priceLevel?.id ?? null,
                amount: itemLine.amount,
                taxAmount: itemLine.taxAmount,
                tax2Amount: null,
                departmentId: itemLine.department?.id ?? null,
                classId: itemLine.class?.id ?? null,
                locationId: itemLine.location?.id ?? null,
                lineNumber: itemLine.lineNumber,
                taxCodeId: itemLine.taxCode?.id ?? null,
                customFields: getCustomFieldsTransfer(itemLine.customFields),
            });
        }

        return lines;
    }, []);

export const parseDiscountRate = (discountRateFieldValue?: string) => {
    if (!discountRateFieldValue?.trim()) {
        return {
            discountRate: null,
            isDiscountRateAbsolute: null,
        };
    }

    const isDiscountRateAbsolute = !discountRateFieldValue.trim().endsWith('%');
    const discountRate = Number(
        isDiscountRateAbsolute ? discountRateFieldValue : discountRateFieldValue.trim().slice(0, -1)
    );

    if (!numberHelpers.isNumber(discountRate)) {
        return {
            discountRate: null,
            isDiscountRateAbsolute: null,
        };
    }

    return {
        discountRate,
        isDiscountRateAbsolute,
    };
};

export const isDiscountRateValid = (discountRateFieldValue?: string) => {
    if (!discountRateFieldValue?.trim()) {
        return true;
    }

    const { discountRate } = parseDiscountRate(discountRateFieldValue);

    return Boolean(discountRate);
};

const getItemListValidationErrors = (data: {
    itemList: NetSuiteInvoiceListItemExpanded[];
    fieldSettings: ReturnType<typeof useNetSuiteInvoiceFieldSettings>;
    customFieldsList: domain.Field[];
    workflowId: string;
    errors: string[];
}) => {
    const { itemList, fieldSettings, customFieldsList, workflowId, errors } = data;

    itemList.forEach((line) => {
        if (checkInvoiceListItemIsEmpty(line)) {
            return;
        }

        if (
            !line.item ||
            !line.quantity ||
            !numberHelpers.isNumber(line.amount) ||
            isMandatoryFieldEmpty(line.description, fieldSettings.itemsDescription) ||
            isMandatoryFieldEmpty(line.units, fieldSettings.itemsUnit) ||
            isMandatoryFieldEmpty(line.priceLevel, fieldSettings.itemsPriceLevel) ||
            isMandatoryFieldEmpty(line.taxCode, fieldSettings.itemsTaxCode) ||
            isMandatoryFieldEmpty(line.class, fieldSettings.itemsClass) ||
            isMandatoryFieldEmpty(line.department, fieldSettings.itemsDepartment) ||
            isMandatoryFieldEmpty(line.location, fieldSettings.itemsLocation)
        ) {
            errors.push(messages.mandatoryFieldsError);
        }

        validateCustomFields({
            customFieldsValues: line.customFields || [],
            customFieldsLevels: ['Lines', 'Header And Lines'],
            customFieldsList,
            workflowId,
            errors,
        });
    });
};

export const getInvoiceValidationErrors = (data: {
    fields: Partial<NetSuiteInvoiceFields>;
    customFieldsValues: CustomFieldsState;
    customFieldsList: domain.Field[];
    workflowId: string;
    fieldSettings: ReturnType<typeof useNetSuiteInvoiceFieldSettings>;
    companyCurrency: string;
    requestCurrencyISOCode: string | null;
    defaultCurrencyRate: number | undefined;
    itemList: NetSuiteInvoiceListItemExpanded[];
    attachmentsFieldSettings: ReturnType<typeof useNetSuiteAttachmentsFieldSettings>;
    attachments: domain.RequestAttachment[];
}) => {
    const {
        fields,
        customFieldsValues,
        customFieldsList,
        workflowId,
        companyCurrency,
        requestCurrencyISOCode,
        fieldSettings,
        defaultCurrencyRate,
        itemList,
        attachmentsFieldSettings,
        attachments,
    } = data;

    const errors: string[] = [];

    if (
        !fields.customer ||
        !fields.transactionDate ||
        isMandatoryFieldEmpty(fields.billingAddress, fieldSettings.billingAddress) ||
        isMandatoryFieldEmpty(fields.transactionReference, fieldSettings.reference) ||
        isMandatoryFieldEmpty(fields.dueDate, fieldSettings.dueDate) ||
        isMandatoryFieldEmpty(fields.startDate, fieldSettings.startDate) ||
        isMandatoryFieldEmpty(fields.endDate, fieldSettings.endDate) ||
        isMandatoryFieldEmpty(fields.terms, fieldSettings.terms) ||
        isMandatoryFieldEmpty(fields.class, fieldSettings.class) ||
        isMandatoryFieldEmpty(fields.department, fieldSettings.department) ||
        isMandatoryFieldEmpty(fields.location, fieldSettings.location) ||
        isMandatoryFieldEmpty(fields.discountItem, fieldSettings.discountItem) ||
        isMandatoryFieldEmpty(fields.discountRate, fieldSettings.discountRate) ||
        isMandatoryFieldEmpty(fields.memo, fieldSettings.memo) ||
        isMandatoryFieldEmpty(fields.salesRep, fieldSettings.salesRep) ||
        isMandatoryFieldEmpty(fields.opportunity, fieldSettings.opportunity) ||
        isMandatoryFieldEmpty(fields.salesEffectiveDate, fieldSettings.salesEffectiveDate) ||
        isMandatoryFieldEmpty(fields.partner, fieldSettings.partner) ||
        isMandatoryFieldEmpty(fields.leadSource, fieldSettings.leadSource) ||
        isMandatoryFieldEmpty(fields.shipDate, fieldSettings.shippingDate) ||
        isMandatoryFieldEmpty(fields.shipMethod, fieldSettings.shippingMethod) ||
        isMandatoryFieldEmpty(fields.shippingAddress, fieldSettings.shippingAddress)
    ) {
        errors.push(messages.mandatoryFieldsError);
    }

    if (isFieldMandatory(attachmentsFieldSettings.attachments) && attachments.length === 0) {
        errors.push(messages.mandatoryFieldsError);
    }

    if (!isDiscountRateValid(fields.discountRate)) {
        errors.push(messages.invalidDiscountRateError);
    }

    if (fields.discountRate && !fields.discountItem) {
        errors.push(messages.emptyDiscountItemError);
    }

    if (
        requestCurrencyISOCode !== companyCurrency &&
        !numberHelpers.isNumber(fields.exchangeRate) &&
        !numberHelpers.isNumber(defaultCurrencyRate)
    ) {
        errors.push(messages.exchangeRateError);
    }

    getItemListValidationErrors({ itemList, fieldSettings, customFieldsList, workflowId, errors });

    validateCustomFields({
        customFieldsValues: Object.values(customFieldsValues),
        customFieldsLevels: ['Header', 'Header And Lines'],
        customFieldsList,
        workflowId,
        errors,
    });

    return uniq(errors);
};

export const mapCustomerData = (customer: GetNetSuiteCustomer['response'] | undefined) => {
    if (!customer || !customer.id || !customer.name) {
        return null;
    }

    const customerData = {
        id: customer.id,
        text: customer.name,
        addresses: mapCustomerAddresses(customer.addresses),
        currency: domain.schemas.mapReference(customer.currency),
        salesRep: domain.schemas.mapReference(customer.salesRep),
        leadSource: domain.schemas.mapReference(customer.leadSource),
        partner: domain.schemas.mapReference(customer.partner),
        taxNumber: customer.taxNumber ?? null,
    };

    return customerData;
};
